/* @jsx h */
import { defineComponent, h, ref, onMounted, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { SearchBar } from "../Home/components/SearchBar";
import { CategoryFilter } from "../Home/components/CategoryFilter";
import { RestaurantCard } from "../Home/components/RestaurantCard";
import { RestaurantFilters } from "../Home/components/RestaurantFilters";
import { PublicHeaderComponent } from "../../components/PublicHeaderComponent";
import { PublicFooterComponent } from "../../components/PublicFooterComponent";
import { SeoHead } from "../../components/SeoHead";
import { CitySelector } from "../Home/components/CitySelector";
import { useBranding } from "../../providers/Branding";
import { useLocalization } from "@lemmonhq/uikit";
import {
  fetchEatsGuideRestaurants,
  type EatsGuideCity,
} from "../../../api/client";

interface Restaurant {
  identifier: string;
  slug: string | null;
  name: { en: string | null };
  textTeaser: { en: string | null };
  image: { url: string };
  address: {
    streetAddress: string;
    addressLocality: string;
  };
  category: Record<string, any>;
  openingHours: string | string[];
  features?: string[];
  googleRating?: number | null;
  googleReviewCount?: number | null;
}

export const AllRestaurantsPage = defineComponent({
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { branding, defaultCity, cities: brandingCities } = useBranding();
    const { t } = useLocalization();
    const restaurants = ref<Restaurant[]>([]);
    const loading = ref(true);
    const searchQuery = ref("");
    const selectedCategory = ref("all");
    const selectedFeatures = ref<string[]>([]);
    const minGoogleRating = ref(0);
    const selectedCity = ref(defaultCity.value || "lausanne");
    const currentCityInfo = ref<EatsGuideCity | null>(null);

    const loadRestaurants = async (city: string) => {
      try {
        loading.value = true;
        const response = await fetchEatsGuideRestaurants(city);
        currentCityInfo.value = response.city;

        // Filter out restaurants with missing essential data
        const isZurich = city.toLowerCase() === "zurich";
        restaurants.value = (response.data as Restaurant[]).filter(
          (restaurant: any) => {
            const hasName = restaurant.name?.en;
            const hasAddress =
              restaurant.address?.streetAddress ||
              restaurant.address?.addressLocality;

            if (!hasName || !hasAddress) return false;

            // For Zurich, filter out placeholder images
            if (isZurich) {
              const hasValidImage =
                restaurant.image?.url &&
                !restaurant.image.url.includes(
                  "img_placeholder_test_mit_rahmen_grauer_hintergrund_1280x960.png",
                );
              const hasTeaser = restaurant.textTeaser?.en;
              if (!hasValidImage || !hasTeaser) return false;
            }

            return true;
          },
        );
      } catch (error) {
        console.error("Failed to load restaurant data:", error);
        restaurants.value = [];
      } finally {
        loading.value = false;
      }
    };

    const handleCityChange = async (city: string) => {
      selectedCity.value = city;
      router.push({ path: `/restaurants/${city}`, query: route.query });
      await loadRestaurants(city);
    };

    onMounted(async () => {
      const urlCity =
        (route.params.city as string) || defaultCity.value || "lausanne";
      selectedCity.value = urlCity;
      await loadRestaurants(urlCity);
    });

    watch(
      () => route.params.city,
      async (newCity) => {
        if (
          newCity &&
          typeof newCity === "string" &&
          newCity !== selectedCity.value
        ) {
          selectedCity.value = newCity;
          await loadRestaurants(newCity);
        }
      },
    );

    const categories = computed(() => {
      const categoryMap = new Map<string, number>();

      restaurants.value.forEach((restaurant) => {
        Object.keys(restaurant.category || {}).forEach((category) => {
          if (
            ![
              "Gastronomy",
              "Restaurants",
              "Meal",
              "Nightlife",
              "Bars & Lounges",
            ].includes(category)
          ) {
            categoryMap.set(category, (categoryMap.get(category) || 0) + 1);
          }
        });
      });

      const categoryIcons: Record<string, string> = {
        "Coffee Houses & Tea Rooms": "local_cafe",
        Italian: "local_pizza",
        Asian: "ramen_dining",
        Sushi: "set_meal",
        "Swiss Specialties": "lunch_dining",
        Fish: "fish_icon",
        Brunch: "brunch_dining",
        Lunch: "lunch_dining",
        Dinner: "dinner_dining",
        Coffee: "local_cafe",
        Cakes: "cake",
        "Cocktail Bar": "local_bar",
      };

      return Array.from(categoryMap.entries())
        .map(([name, count]) => ({
          name,
          count,
          icon: categoryIcons[name] || "restaurant",
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 8);
    });

    const hasGoogleRatings = computed(() => {
      return restaurants.value.some(
        (r) => r.googleRating && r.googleRating > 0,
      );
    });

    const filteredRestaurants = computed(() => {
      let filtered = restaurants.value;

      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        filtered = filtered.filter(
          (restaurant) =>
            restaurant.name.en?.toLowerCase().includes(query) ||
            restaurant.textTeaser?.en?.toLowerCase().includes(query) ||
            Object.keys(restaurant.category || {}).some((cat) =>
              cat.toLowerCase().includes(query),
            ),
        );
      }

      if (selectedCategory.value !== "all") {
        filtered = filtered.filter(
          (restaurant) => restaurant.category?.[selectedCategory.value],
        );
      }

      // Feature filters — restaurant must have ALL selected features
      if (selectedFeatures.value.length > 0) {
        filtered = filtered.filter((restaurant) => {
          const features = restaurant.features || [];
          return selectedFeatures.value.every((f) => features.includes(f));
        });
      }

      // Google rating filter
      if (minGoogleRating.value > 0) {
        filtered = filtered.filter(
          (restaurant) =>
            restaurant.googleRating &&
            restaurant.googleRating >= minGoogleRating.value,
        );
      }

      return filtered;
    });

    const handleSearch = (query: string) => {
      searchQuery.value = query;
    };

    const handleCategoryChange = (category: string) => {
      selectedCategory.value = category;
    };

    const currentCityName = computed(() => {
      return currentCityInfo.value?.name || selectedCity.value;
    });

    const structuredData = computed(() => ({
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: t("customer.directory.restaurants_structured_name", [
        currentCityName.value,
      ]),
      description: t("customer.directory.restaurants_structured_description", [
        currentCityName.value,
      ]),
      numberOfItems: filteredRestaurants.value.length,
    }));

    return () => (
      <div class="min-h-screen">
        <SeoHead
          title={t("customer.directory.restaurants_seo_title", [
            currentCityName.value,
            branding.value?.name || "",
          ])}
          description={t("customer.directory.restaurants_seo_description", [
            currentCityName.value,
          ])}
          keywords={`restaurants ${currentCityName.value}, all restaurants, dining, food guide`}
          structuredData={structuredData.value}
        />
        <PublicHeaderComponent>
          {brandingCities.value.length > 1 && (
            <CitySelector
              cities={brandingCities.value}
              selectedCity={selectedCity.value}
              onCityChange={handleCityChange}
            />
          )}
        </PublicHeaderComponent>

        <main class="max-w-7xl mx-auto px-4">
          {/* Page Header */}
          <div class="text-center py-12">
            <h1 class="text-4xl font-serif font-light text-gray-900 mb-4">
              {t("customer.directory.all_restaurants_in", [
                currentCityName.value,
              ])}
            </h1>
            <p class="text-gray-600 font-light max-w-2xl mx-auto">
              {t("customer.directory.restaurants_subtitle", [
                currentCityName.value,
              ])}
            </p>
          </div>

          {/* Search Bar */}
          <SearchBar onSearch={handleSearch} />

          {/* Restaurant Filters */}
          <RestaurantFilters
            selectedFeatures={selectedFeatures.value}
            minGoogleRating={minGoogleRating.value}
            hasGoogleRatings={hasGoogleRatings.value}
            onUpdate:selectedFeatures={(val: string[]) =>
              (selectedFeatures.value = val)
            }
            onUpdate:minGoogleRating={(val: number) =>
              (minGoogleRating.value = val)
            }
          />

          <div class="hidden">
            {/* Category Filter */}
            <CategoryFilter
              categories={categories.value}
              selectedCategory={selectedCategory.value}
              onUpdate:selectedCategory={handleCategoryChange}
            />
          </div>

          {/* Restaurant Grid */}
          <div class="mb-16">
            <p class="text-gray-500 font-light text-sm mb-6">
              {loading.value
                ? t("customer.home.loading")
                : t(
                    filteredRestaurants.value.length === 1
                      ? "customer.directory.restaurant_found_one"
                      : "customer.directory.restaurant_found_many",
                    [filteredRestaurants.value.length],
                  )}
            </p>

            {loading.value ? (
              <div class="text-center py-16">
                <div class="text-4xl mb-4">🍽️</div>
                <p class="text-gray-600">
                  {t("customer.directory.loading_restaurants")}
                </p>
              </div>
            ) : filteredRestaurants.value.length > 0 ? (
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                {filteredRestaurants.value.map((restaurant) => (
                  <RestaurantCard
                    key={restaurant.identifier}
                    restaurant={restaurant}
                    city={selectedCity.value}
                  />
                ))}
              </div>
            ) : (
              <div class="text-center py-16">
                <div class="text-6xl mb-6">🔍</div>
                <h3 class="text-2xl font-serif font-light text-gray-900 mb-4">
                  {t("customer.home.no_restaurants_found")}
                </h3>
                <p class="text-gray-600 font-light max-w-md mx-auto">
                  {searchQuery.value || selectedCategory.value !== "all"
                    ? t("customer.directory.adjust_search_or_categories")
                    : t("customer.directory.no_restaurants_listed")}
                </p>
              </div>
            )}
          </div>
        </main>

        <PublicFooterComponent />
      </div>
    );
  },
});
